// Texto 25 (etapa 3): Meus clientes › Relatório (E7), no visual do Relatório de estudos (◀ período ▶, total grande, barras).
// Um cliente ou todos, por período: vendeu, recebeu, falta receber, melhores clientes e atrasados.
// E8: o mesmo em planilha (.xlsx). F5: sem CPF/CNPJ completo, endereço completo nem nascimento, a não ser que marque
// "Incluir documentos" (pede a senha). Anotações nunca saem no relatório.
import { useState } from 'react';
import { Alert, Pressable, ScrollView, Switch, Text, View } from 'react-native';
import type { Reg } from '../store/types';
import { useDoc } from '../store/store';
import { MES_CURTO, addDays, brYmd, nomeMes, parseYmd, today, ymd } from '../lib/date';
import { N_, t } from '../lib/i18n';
import { dinheiro, moedaPadrao } from '../lib/moeda';
import { gerarECompartilharPdf, type BlocoPdf } from '../lib/pdf';
import { compartilharXlsx } from '../lib/planilha';
import { marcarAbaAtual } from '../lib/relatorios';
import { atrasada, falta, parcelasDe, quitada, soAVista } from '../lib/recebimentos';
import { enderecoTexto } from '../lib/clientes';
import { formatarDoc, mascararDoc } from '../modulos/clientes';
import { useConfirmarIdentidade } from './ConfirmarIdentidade';
import { Btn, Chip, Field, Sheet } from './ui';
import { PickerModal } from './Pickers';
import { RelatoriosGerados } from './RelatoriosGerados';
import { Barras, CartaoTotal, NavPeriodo } from './relatorio/Periodo';
import { C } from './theme';

type Modo = 'mes' | 'ano' | 'datas';
const inicioMes = (s: string) => s.slice(0, 8) + '01';
const fimMes = (s: string) => { const d = parseYmd(inicioMes(s)); return ymd(new Date(d.getFullYear(), d.getMonth() + 1, 0)); };
const somaMes = (s: string, n: number) => { const d = parseYmd(inicioMes(s)); return ymd(new Date(d.getFullYear(), d.getMonth() + n, 1)); };
const inicioDe = (m: Modo, s: string) => (m === 'mes' ? inicioMes(s) : m === 'ano' ? s.slice(0, 4) + '-01-01' : s);
const fimDe = (m: Modo, ini: string, fimEsc: string) => (m === 'mes' ? fimMes(ini) : m === 'ano' ? ini.slice(0, 4) + '-12-31' : fimEsc);
const num = (v: unknown) => Number(v) || 0;
const r2 = (n: number) => Math.round(n * 100) / 100;
const dataDe = (r: Reg) => String(r.c.data || r.criado.slice(0, 10));
const br = (d: unknown) => (d ? brYmd(String(d)) : '');

/** Os números do relatório (exportada para o teste). Recebido no período: parcelas com "pago em" dentro do período. */
export function numerosClientes(regs: Reg[], ini: string, fim: string, cid: string | null, hoje = today()) {
  const clientes = regs.filter(r => r.col === 'cliente' && (!cid || r.id === cid));
  const ids = new Set(clientes.map(c => c.id));
  const recebs = regs.filter(r => r.col === 'receb' && r.pai && ids.has(r.pai));
  const vendas = recebs.filter(r => dataDe(r) >= ini && dataDe(r) <= fim).sort((a, b) => dataDe(a).localeCompare(dataDe(b)));
  const pagas = recebs.flatMap(r => parcelasDe(r).filter(p => num(p.recebido) > 0 && p.pagoEm && p.pagoEm >= ini && p.pagoEm <= fim).map(p => ({ r, p })));
  const abertas = recebs.flatMap(r => parcelasDe(r).filter(p => !quitada(p)).map(p => ({ r, p })))
    .sort((a, b) => String(a.p.venc || '9999').localeCompare(String(b.p.venc || '9999')));
  const vendeu = r2(vendas.reduce((s, r) => s + num(r.c.valor), 0));
  const recebeu = r2(pagas.reduce((s, x) => s + num(x.p.recebido), 0));
  const faltaReceber = r2(abertas.reduce((s, x) => s + falta(x.p), 0));
  const atrasadas = abertas.filter(x => atrasada(x.p, hoje));
  const porCliente = clientes.map(c => {
    const v = vendas.filter(r => r.pai === c.id), rc = pagas.filter(x => x.r.pai === c.id), ab = abertas.filter(x => x.r.pai === c.id);
    return { c, vendeu: r2(v.reduce((s, r) => s + num(r.c.valor), 0)), compras: v.length, recebeu: r2(rc.reduce((s, x) => s + num(x.p.recebido), 0)),
      falta: r2(ab.reduce((s, x) => s + falta(x.p), 0)), atrasado: r2(ab.filter(x => atrasada(x.p, hoje)).reduce((s, x) => s + falta(x.p), 0)) };
  });
  const melhores = porCliente.filter(x => x.vendeu > 0).sort((a, b) => b.vendeu - a.vendeu).slice(0, 10);
  return { clientes, vendas, pagas, abertas, atrasadas, vendeu, recebeu, faltaReceber, porCliente, melhores };
}

export function RelatorioClientes({ visivel, cor, onFechar, clienteId = null }: { visivel: boolean; cor: string; onFechar: () => void; clienteId?: string | null }) {
  const regs = useDoc('clientes')?.regs ?? [];
  const fin = useDoc('financas');
  const moeda = fin?.moeda || moedaPadrao();
  const [modo, setModo] = useState<Modo>('mes');
  const [ini, setIni] = useState(() => inicioMes(today()));
  const [fimEsc, setFimEsc] = useState(() => today());
  const [escolher, setEscolher] = useState<'ini' | 'fim' | null>(null);
  const [cid, setCid] = useState<string | null>(clienteId);
  const [busca, setBusca] = useState('');
  const [docs, setDocs] = useState(false);
  const [gerando, setGerando] = useState(false);
  const ident = useConfirmarIdentidade(cor);
  const din = (v: number) => dinheiro(v, moeda);

  const fim = fimDe(modo, ini, fimEsc < ini ? ini : fimEsc);
  const n = numerosClientes(regs, ini, fim, cid);
  const so1 = cid ? n.clientes[0] ?? null : null;
  const todos = regs.filter(r => r.col === 'cliente').sort((a, b) => String(a.c.nome).localeCompare(String(b.c.nome)));
  const achados = busca.trim() ? todos.filter(r => String(r.c.nome ?? '').toLowerCase().includes(busca.trim().toLowerCase())).slice(0, 8) : [];
  const vista = soAVista();
  // barras: recebido por dia (mês) ou por mês (ano, ou datas com mais de 2 meses)
  const nDias = Math.round((parseYmd(fim).getTime() - parseYmd(ini).getTime()) / 86400000) + 1;
  const porMes = modo === 'ano' || nDias > 62;
  const barras = porMes
    ? (() => { const ms: string[] = []; for (let m = inicioMes(ini); m <= fim; m = somaMes(m, 1)) ms.push(m); return ms; })()
      .map(m => ({ v: n.pagas.filter(x => x.p.pagoEm!.slice(0, 7) === m.slice(0, 7)).reduce((s, x) => s + num(x.p.recebido), 0), rot: t(MES_CURTO[parseYmd(m).getMonth()]) }))
    : Array.from({ length: nDias }, (_, i) => { const d = addDays(ini, i); return { v: n.pagas.filter(x => x.p.pagoEm === d).reduce((s, x) => s + num(x.p.recebido), 0), rot: i === 0 || (i + 1) % 5 === 0 ? String(parseYmd(d).getDate()) : '' }; });
  const atual = modo !== 'datas' && ini === inicioDe(modo, today());
  const nomeMesAno = (s: string) => { const d = parseYmd(s); return t('{mes} de {ano}', { mes: nomeMes(d.getMonth()), ano: d.getFullYear() }); };
  const titulo = modo === 'mes' ? (atual ? t('Este mês') : nomeMesAno(ini)) : modo === 'ano' ? (atual ? t('Este ano') : ini.slice(0, 4)) : t('Datas escolhidas');
  const mudarModo = (m: Modo) => { if (m === modo) return; setModo(m); if (m === 'datas') { setFimEsc(fim); return; } setIni(inicioDe(m, today())); };
  const andar = (k: number) => setIni(modo === 'mes' ? somaMes(ini, k) : `${Number(ini.slice(0, 4)) + k}-01-01`);
  const nomeDe = (id: string | null | undefined) => String(regs.find(r => r.id === id)?.c.nome ?? '');

  // F5: só com "Incluir documentos" (e a senha) sai o documento completo, o endereço completo e o nascimento
  const marcarDocs = async (v: boolean) => { if (!v) return setDocs(false); if (await ident.pedir(t('Incluir documentos no relatório'))) setDocs(true); };
  const docTxt = (c: Reg) => (c.c.doc ? (docs ? formatarDoc(c.c.doc) : mascararDoc(c.c.doc)) : '');
  const endTxt = (c: Reg) => (docs ? enderecoTexto(c.c) || String(c.c.enderecoLivre ?? '') : [c.c.cidade, c.c.uf].filter(Boolean).join(' – '));
  const comoTxt = (r: Reg) => [r.c.como && t(String(r.c.como)), r.c.forma && t(String(r.c.forma))].filter(Boolean).join(' · ');
  const faltaDe = (r: Reg) => r2(parcelasDe(r).reduce((s, p) => s + falta(p), 0));
  const periodo = `${brYmd(ini)} – ${brYmd(fim)}`;
  const nomeArq = `${t('Relatório de clientes')} ${brYmd(ini)} a ${brYmd(fim)}${so1 ? ' ' + String(so1.c.nome ?? '') : ''}`;

  const exportarPdf = async () => {
    if (gerando) return;
    setGerando(true);
    try {
      const b: BlocoPdf[] = [
        { t: 'titulo', texto: t('Relatório de clientes') },
        { t: 'sub', texto: `${titulo} · ${periodo} · ${so1 ? String(so1.c.nome ?? '') : t('Todos os clientes')}` },
        { t: 'linha' },
      ];
      if (so1) {
        b.push(...[[t('Telefone'), so1.c.telefone], [t('E-mail'), so1.c.email], [so1.c.tipo === 'Empresa' ? 'CNPJ' : 'CPF', docTxt(so1)], [t('Endereço'), endTxt(so1)],
          [t('Data de nascimento'), docs && so1.c.tipo !== 'Empresa' ? br(so1.c.nascimento) : ''], [t('Cliente desde'), br(so1.criado.slice(0, 10))]]
          .filter(([, v]) => String(v ?? '').trim()).map(([k, v]) => ({ t: 'par', texto: `${k}: ${v}` } as BlocoPdf)), { t: 'espaco', h: 4 });
      }
      b.push({ t: 'par', negrito: true, texto: t('Vendeu no período: {v}', { v: din(n.vendeu) }) },
        { t: 'par', texto: t('Recebeu no período: {v}', { v: din(n.recebeu) }) });
      if (!vista) b.push({ t: 'par', texto: t('Falta receber (hoje): {v}', { v: din(n.faltaReceber) }) },
        { t: 'par', texto: t('Atrasado (hoje): {v}', { v: din(r2(n.atrasadas.reduce((s, x) => s + falta(x.p), 0))) }) });
      if (!so1 && n.melhores.length) {
        b.push({ t: 'espaco', h: 6 }, { t: 'par', negrito: true, texto: t('Melhores clientes') },
          { t: 'tabela', colunas: [t('Cliente'), t('Compras'), t('Vendeu'), t('Recebeu'), t('Falta')], larguras: [0.36, 0.12, 0.18, 0.17, 0.17],
            linhas: n.melhores.map(x => [String(x.c.c.nome ?? ''), String(x.compras), din(x.vendeu), din(x.recebeu), x.falta ? din(x.falta) : '—']) });
      }
      b.push({ t: 'espaco', h: 6 }, { t: 'par', negrito: true, texto: t('Vendas e atendimentos') },
        n.vendas.length ? { t: 'tabela', colunas: [t('Data'), ...(so1 ? [] : [t('Cliente')]), t('O que foi'), t('Valor'), t('Como pagou'), t('Falta')],
          larguras: so1 ? [0.14, 0.36, 0.16, 0.2, 0.14] : [0.12, 0.22, 0.26, 0.14, 0.14, 0.12],
          linhas: n.vendas.map(r => [br(dataDe(r)), ...(so1 ? [] : [nomeDe(r.pai)]), String(r.c.desc || t('Venda')), din(num(r.c.valor)), comoTxt(r), faltaDe(r) ? din(faltaDe(r)) : '—']) }
          : { t: 'par', texto: t('Nada neste período.') });
      if (!vista && n.atrasadas.length) {
        b.push({ t: 'espaco', h: 6 }, { t: 'par', negrito: true, texto: t('Atrasados') },
          { t: 'tabela', colunas: [t('Cliente'), t('Parcela'), t('Venceu'), t('Falta')], larguras: [0.4, 0.2, 0.2, 0.2],
            linhas: n.atrasadas.map(x => [nomeDe(x.r.pai), `${String(x.r.c.desc || t('Venda'))} ${x.p.rotulo}`, br(x.p.venc), din(falta(x.p))]) });
      }
      marcarAbaAtual('clientes');   // o PDF entra no histórico de relatórios da aba
      await gerarECompartilharPdf(nomeArq, b, 'My Planner');
    } catch (e: any) {
      Alert.alert(t('Não foi possível gerar o PDF.'), String(e?.message ?? e));
    } finally { setGerando(false); }
  };

  const exportarPlanilha = async () => {
    if (gerando) return;
    setGerando(true);
    try {
      const pc = new Map(n.porCliente.map(x => [x.c.id, x]));
      const colCli = [t('Nome'), t('Tipo'), 'CPF/CNPJ', t('Telefone'), t('Outro telefone'), t('E-mail'), ...(docs ? [t('Endereço'), t('CEP')] : []), t('Cidade'), t('Estado (UF)'),
        ...(docs ? [t('Data de nascimento')] : []), t('Situação'), t('Etiquetas'), t('Como me conheceu'), t('Autorizou receber mensagens'), t('Cliente desde'),
        t('Vendeu no período'), t('Recebeu no período'), t('Falta receber'), t('Atrasado')];
      const linhasCli = n.clientes.slice().sort((a, b) => String(a.c.nome).localeCompare(String(b.c.nome))).map(c => {
        const x = pc.get(c.id)!;
        return [String(c.c.nome ?? ''), t(String(c.c.tipo || 'Pessoa')), docTxt(c), String(c.c.telefone ?? ''), String(c.c.telefone2 ?? ''), String(c.c.email ?? ''),
          ...(docs ? [enderecoTexto(c.c) || String(c.c.enderecoLivre ?? ''), String(c.c.cep ?? '')] : []), String(c.c.cidade ?? ''), String(c.c.uf ?? ''),
          ...(docs ? [c.c.tipo === 'Empresa' ? '' : br(c.c.nascimento)] : []), t(String(c.c.situacao || 'Ativo')), String(c.c.etiquetas ?? ''), c.c.comoConheceu ? t(String(c.c.comoConheceu)) : '',
          c.c.autoriza ? t('Sim') : t('Não'), br(c.criado.slice(0, 10)), x.vendeu, x.recebeu, x.falta, x.atrasado] as (string | number)[];
      });
      await compartilharXlsx(nomeArq, [
        { nome: t('Clientes'), colunas: colCli, linhas: linhasCli },
        { nome: t('Vendas e atendimentos'), colunas: [t('Data'), t('Cliente'), t('O que foi'), t('Valor'), t('Como pagou'), t('Falta')],
          linhas: n.vendas.map(r => [br(dataDe(r)), nomeDe(r.pai), String(r.c.desc || t('Venda')), num(r.c.valor), comoTxt(r), faltaDe(r)]) },
        { nome: t('Recebido no período'), colunas: [t('Pago em'), t('Cliente'), t('O que foi'), t('Parcela'), t('Valor')],
          linhas: n.pagas.slice().sort((a, b) => String(a.p.pagoEm).localeCompare(String(b.p.pagoEm))).map(x => [br(x.p.pagoEm), nomeDe(x.r.pai), String(x.r.c.desc || t('Venda')), x.p.rotulo, num(x.p.recebido)]) },
        ...(vista ? [] : [{ nome: t('Falta receber'), colunas: [t('Cliente'), t('O que foi'), t('Parcela'), t('Vence'), t('Falta'), t('Atrasada')],
          linhas: n.abertas.map(x => [nomeDe(x.r.pai), String(x.r.c.desc || t('Venda')), x.p.rotulo, x.p.venc ? br(x.p.venc) : t('na entrega'), falta(x.p), atrasada(x.p) ? t('Sim') : t('Não')] as (string | number)[]) }]),
      ]);
    } catch (e: any) {
      Alert.alert(t('Não foi possível gerar a planilha.'), String(e?.message ?? e));
    } finally { setGerando(false); }
  };

  const titSec = (s: string) => <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{s}</Text>;
  const linha = (k: string, tit: string, sub: string, dir: string, corDir?: string) => (
    <View key={k} style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, flexDirection: 'row', gap: 8, alignItems: 'center' }}>
      <View style={{ flex: 1 }}><Text style={{ color: C.text, fontWeight: '700' }}>{tit}</Text>{!!sub && <Text style={{ color: C.sub, fontSize: 12 }}>{sub}</Text>}</View>
      <Text style={{ color: corDir ?? C.text, fontWeight: '800' }}>{dir}</Text>
    </View>);
  return (
    <Sheet visible={visivel} onClose={onFechar} title={t('Relatório de clientes')} larga>
      <ScrollView contentContainerStyle={{ padding: 16, gap: 14, paddingBottom: 60 }} keyboardShouldPersistTaps="handled">
        <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
          {([['mes', N_('Mês')], ['ano', N_('Ano')], ['datas', N_('Datas à escolha')]] as [Modo, string][]).map(([m, l]) =>
            <Chip key={m} small cor={cor} on={modo === m} label={t(l)} onPress={() => mudarModo(m)} />)}
        </View>
        {/* um cliente ou todos */}
        <View style={{ gap: 6 }}>
          <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip small cor={cor} on={!cid} label={t('Todos os clientes')} onPress={() => setCid(null)} />
            {!!cid && <Chip small cor={cor} on label={'👤 ' + nomeDe(cid)} onPress={() => setCid(null)} />}
          </View>
          <Field value={busca} onChangeText={setBusca} placeholder={t('Buscar um cliente')} />
          {achados.length > 0 && (
            <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
              {achados.map(r => <Chip key={r.id} small cor={cor} on={cid === r.id} label={String(r.c.nome ?? '')} onPress={() => { setCid(r.id); setBusca(''); }} />)}
            </View>
          )}
        </View>
        {modo === 'datas' ? (
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'center' }}>
            <Chip cor={cor} on label={`${t('De')} ${brYmd(ini)}`} onPress={() => setEscolher('ini')} />
            <Chip cor={cor} on label={`${t('Até')} ${brYmd(fim)}`} onPress={() => setEscolher('fim')} />
          </View>
        ) : <NavPeriodo cor={cor} titulo={titulo} datas={periodo} onAnterior={() => andar(-1)} onProximo={() => andar(1)} podeProximo={!atual} />}

        <CartaoTotal valor={din(n.vendeu)} legenda={so1 ? t('{nome} comprou no período', { nome: String(so1.c.nome ?? '') }) : t('vendido no período')}>
          <View style={{ flexDirection: 'row', alignSelf: 'stretch', marginTop: 12, paddingTop: 10, borderTopWidth: 1, borderTopColor: C.card2 }}>
            {([[t('Recebeu'), din(n.recebeu), C.text], ...(vista ? [] : [[t('Falta receber'), din(n.faltaReceber), C.text],
              [t('Atrasado'), din(r2(n.atrasadas.reduce((s, x) => s + falta(x.p), 0))), n.atrasadas.length ? C.danger : C.ok]])] as [string, string, string][]).map(([rot, val, c]) => (
              <View key={rot} style={{ flex: 1, alignItems: 'center' }}>
                <Text style={{ color: c, fontWeight: '800', fontSize: 15 }}>{val}</Text>
                <Text style={{ color: C.sub, fontSize: 11 }}>{rot}</Text>
              </View>))}
          </View>
        </CartaoTotal>

        {titSec(porMes ? t('RECEBIDO POR MÊS') : t('RECEBIDO POR DIA'))}
        <Barras valores={barras.map(x => x.v)} rotulos={barras.map(x => x.rot)} cor={cor} largas={barras.length <= 12} rotuloValor={v => din(v).replace(/,00$/, '')} />

        {!so1 && <>
          {titSec(t('MELHORES CLIENTES'))}
          {!n.melhores.length && <Text style={{ color: C.mute, fontSize: 13 }}>{t('Nada neste período.')}</Text>}
          {n.melhores.map((x, i) => linha(x.c.id, `${i + 1}. ${String(x.c.c.nome ?? '')}`, t('{n} compra(s)', { n: x.compras }) + (x.falta && !vista ? ' · ' + t('deve {v}', { v: din(x.falta) }) : ''), din(x.vendeu)))}
        </>}

        {!vista && n.atrasadas.length > 0 && <>
          {titSec(t('ATRASADOS'))}
          {n.atrasadas.map(x => linha(x.p.id, nomeDe(x.r.pai), `${String(x.r.c.desc || t('Venda'))} ${x.p.rotulo} · ${t('venceu {d}', { d: br(x.p.venc) })}`, din(falta(x.p)), C.danger))}
        </>}

        {titSec(t('VENDAS E ATENDIMENTOS') + ` (${n.vendas.length})`)}
        {!n.vendas.length && <Text style={{ color: C.mute, fontSize: 13 }}>{t('Nada neste período.')}</Text>}
        {n.vendas.slice().reverse().slice(0, 50).map(r => linha(r.id, String(r.c.desc || t('Venda')), [br(dataDe(r)), !so1 && nomeDe(r.pai), comoTxt(r)].filter(Boolean).join(' · '), din(num(r.c.valor))))}

        <Pressable onPress={() => marcarDocs(!docs)} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: C.card, borderRadius: 14, padding: 12 }}>
          <View style={{ flex: 1 }}>
            <Text style={{ color: C.text, fontWeight: '700' }}>{t('Incluir documentos')}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{t('CPF/CNPJ completo, endereço completo e data de nascimento. Pede a senha do app.')}</Text>
          </View>
          <Switch value={docs} onValueChange={marcarDocs} />
        </Pressable>
        <Btn cor={cor} outline disabled={gerando} label={'📄 ' + (gerando ? t('Gerando…') : t('Exportar em PDF'))} onPress={exportarPdf} />
        <Btn cor={cor} outline disabled={gerando} label={'📊 ' + t('Exportar planilha')} onPress={exportarPlanilha} />
        <Text style={{ color: C.mute, fontSize: 11 }}>{docs ? t('Com documentos: guarde o arquivo em lugar seguro.') : t('Sem documentos: o CPF/CNPJ sai só com os 2 últimos números. As anotações nunca saem.')}</Text>
        <RelatoriosGerados aba="clientes" cor={cor} />
      </ScrollView>
      {escolher && <PickerModal mode="date" cor={cor} value={parseYmd(escolher === 'ini' ? ini : fim)}
        onPick={d => { const v = ymd(d); if (escolher === 'ini') { setIni(v); if (v > fimEsc) setFimEsc(v); } else setFimEsc(v < ini ? ini : v); }} onClose={() => setEscolher(null)} />}
      {ident.tela}
    </Sheet>
  );
}
